import React, { useEffect, useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  useRefresh,
  useRedirect,
  useTranslate
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  LinearProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  Bolt,
  EvStation,
  Person,
  Timer,
  AttachMoney,
  LocationOn,
  Schedule,
  Battery0Bar,
  Battery1Bar,
  Battery2Bar,
  Battery3Bar,
  Battery4Bar,
  Battery5Bar,
  Battery6Bar,
  BatteryFull,
  BatteryCharging80,
  Speed,
  StopCircle as StopIcon,
  Refresh,
  FlashOn,
  Power,
  Warning
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { ManualStopDialog } from '../manual-charging/ManualStopDialog';
import { ForceCompleteDialog } from './ForceCompleteDialog';

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'initiating': return 'info';
    case 'charging': return 'success';
    case 'completing': return 'warning';
    case 'completed': return 'default';
    case 'failed': return 'error';
    default: return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'charging': return <Bolt />;
    case 'initiating': return <Schedule />;
    case 'completing': return <Timer />;
    default: return <EvStation />;
  }
};

const detailsTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    verticalAlign: 'top',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
  '& code': {
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
  },
};

// Battery icon based on charge percentage
const BatteryIcon = ({ percentage }: { percentage: number }) => {
  const size = 'large';
  if (percentage <= 0) return <Battery0Bar fontSize={size} />;
  if (percentage <= 14) return <Battery1Bar fontSize={size} />;
  if (percentage <= 28) return <Battery2Bar fontSize={size} />;
  if (percentage <= 42) return <Battery3Bar fontSize={size} />;
  if (percentage <= 57) return <Battery4Bar fontSize={size} />;
  if (percentage <= 71) return <Battery5Bar fontSize={size} />;
  if (percentage <= 85) return <Battery6Bar fontSize={size} />;
  return <BatteryFull fontSize={size} />;
};

const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: { xs: 2, sm: 4 },
      flexWrap: 'wrap',
      gap: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => redirect('/active-sessions')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {translate('resources.active-sessions.show.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={getStatusIcon(record.status)}
              label={translate(`resources.active-sessions.statuses.${record.status}`, { _: record.status?.charAt(0).toUpperCase() + record.status?.slice(1) })}
              color={getStatusColor(record.status) as any}
              size="small"
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {translate('resources.active-sessions.show.sessionNumber')} #{record.app_session_id}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Helper to check if session can be stopped
const canStopSession = (status: string): boolean => {
  return ['initiating', 'charging', 'preparing'].includes(status?.toLowerCase());
};

// Helper to check if session can be force-completed
const canForceCompleteSession = (status: string): boolean => {
  return ['charging', 'idle'].includes(status?.toLowerCase());
};

// Real-time charging progress
const ChargingProgress = ({
  onStopClick,
  onForceCompleteClick
}: {
  onStopClick: () => void;
  onForceCompleteClick: () => void;
}) => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [localDuration, setLocalDuration] = useState<number>(record?.duration_seconds || 0);

  // All active session statuses that should trigger polling
  const activeStatuses = ['initiating', 'preparing', 'charging', 'completing', 'SuspendedEV', 'SuspendedEVSE'];
  const shouldPoll = autoRefresh && record?.status && activeStatuses.includes(record.status);

  // Sync local duration with API value when it changes (±5 second threshold)
  useEffect(() => {
    if (record?.duration_seconds !== undefined) {
      const diff = Math.abs(record.duration_seconds - localDuration);
      // Only sync if difference is more than 5 seconds
      if (diff > 5) {
        setLocalDuration(record.duration_seconds);
      }
    }
  }, [record?.duration_seconds, localDuration]);

  // Local timer to increment duration every second
  useEffect(() => {
    if (shouldPoll) {
      const timer = setInterval(() => {
        setLocalDuration(prev => prev + 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [shouldPoll]);

  // Auto-refresh effect with optimized polling.
  // 30s matches the OCPP server's own meter-values cadence — polling
  // faster just fetches the same data repeatedly without surfacing any
  // newer information, while costing backend + DB resources. The local
  // 1-second duration timer above keeps the on-screen counter ticking
  // smoothly between fetches.
  useEffect(() => {
    if (shouldPoll) {
      const interval = setInterval(() => {
        // Only refresh if document is visible (tab is active)
        if (document.visibilityState === 'visible') {
          refresh();
          setLastUpdated(new Date());
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, refresh]);

  // Refresh immediately when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldPoll) {
        refresh();
        setLastUpdated(new Date());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh, shouldPoll]);

  if (!record) return null;

  const percentage = record.estimated_kwh
    ? Math.min((record.energy_delivered / record.estimated_kwh) * 100, 100)
    : 0;

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden', backgroundColor: record.status === 'charging' ? '#E8F5E9' : '#FFF' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: 2,
            backgroundColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Bolt sx={{ color: 'success.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.active-sessions.show.chargingProgress')}</Typography>
            {shouldPoll && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Refresh sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {translate('resources.active-sessions.details.auto_refresh')} {format(lastUpdated, 'HH:mm:ss')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {shouldPoll && (
              <Chip
                label={translate('resources.active-sessions.details.live')}
                color="success"
                size="small"
                sx={{
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  }
                }}
              />
            )}
            {canStopSession(record.status) && (
              isMobile ? (
                <IconButton size="small" color="error" onClick={onStopClick}>
                  <StopIcon />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={onStopClick}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#D32F2F',
                    },
                  }}
                >
                  {translate('resources.active-sessions.actions.stopSession')}
                </Button>
              )
            )}
            {canForceCompleteSession(record.status) && (
              isMobile ? (
                <IconButton size="small" color="warning" onClick={onForceCompleteClick}>
                  <Warning />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  startIcon={<Warning />}
                  onClick={onForceCompleteClick}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#F57C00',
                    },
                  }}
                >
                  {translate('resources.active-sessions.actions.forceComplete')}
                </Button>
              )
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2 }}>
          <BatteryIcon percentage={percentage} />
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 20,
                borderRadius: 2,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: percentage > 80 ? 'success.main' : 'primary.main'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2">
                {record.energy_delivered?.toFixed(2) || '0.00'} {translate('resources.active-sessions.show.kwhDelivered')}
              </Typography>
              {record.estimated_kwh && (
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.active-sessions.details.target')}: {record.estimated_kwh.toFixed(2)} kWh
                </Typography>
              )}
            </Box>
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            {percentage.toFixed(0)}%
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, mt: 3, flexWrap: 'wrap' }}>
          {record.status !== 'completed' && record.status !== 'failed' && (
            <>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Speed sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{translate('resources.active-sessions.details.current_power')}</Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {record.current_power?.toFixed(2) || '0.00'} kW
                </Typography>
              </Box>
              {record.current_voltage && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Power sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">{translate('resources.active-sessions.show.currentVoltage')}</Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                    {record.current_voltage.toFixed(1)} V
                  </Typography>
                </Box>
              )}
              {record.current_amps && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <FlashOn sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">{translate('resources.active-sessions.show.currentAmps')}</Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                    {record.current_amps.toFixed(1)} A
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Timer sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{translate('resources.active-sessions.details.duration')}</Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {formatDuration(localDuration)}
            </Typography>
          </Box>
          {record.soc_percentage !== null && record.soc_percentage !== undefined && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <BatteryCharging80 sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{translate('resources.active-sessions.show.batterySoc')}</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                {record.soc_percentage}%
              </Typography>
            </Box>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <AttachMoney sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {record.status === 'completed' ? translate('resources.active-sessions.show.finalCost') : translate('resources.active-sessions.details.estimated_cost')}
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              HKD {(record.status === 'completed' ? record.final_cost : record.estimated_cost)?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
        </Box>

        {record.estimated_completion_time && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {translate('resources.active-sessions.show.estimatedCompletion')}: {format(new Date(record.estimated_completion_time), 'PPp')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Session Information Card
const SessionInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <EvStation sx={{ color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.active-sessions.show.sessionInfo')}</Typography>
        </Box>
        <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={detailsTableSx}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.sessionId')}</strong></TableCell>
              <TableCell><code>{record.app_session_id}</code></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.transactionId')}</strong></TableCell>
              <TableCell><code>{record.transaction_id}</code></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.status')}</strong></TableCell>
              <TableCell>
                <Chip
                  label={translate(`resources.active-sessions.statuses.${record.status}`, { _: record.status })}
                  color={getStatusColor(record.status) as any}
                  size="small"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.startTime')}</strong></TableCell>
              <TableCell>
                {record.start_timestamp
                  ? format(new Date(record.start_timestamp), 'PPpp')
                  : '-'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.stopTime')}</strong></TableCell>
              <TableCell>
                {record.charging_completed_at
                  ? format(new Date(record.charging_completed_at), 'PPpp')
                  : translate('resources.active-sessions.details.still_charging')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.lastUpdate')}</strong></TableCell>
              <TableCell>
                {record.last_meter_update
                  ? format(new Date(record.last_meter_update), 'PPpp')
                  : '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Charger Information Card
const ChargerInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'info.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <LocationOn sx={{ color: 'info.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.active-sessions.show.chargerLocation')}</Typography>
        </Box>
        <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={detailsTableSx}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.chargerName')}</strong></TableCell>
              <TableCell>{record.charger_name || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.chargerId')}</strong></TableCell>
              <TableCell><code>{record.charger_id}</code></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.connectorId')}</strong></TableCell>
              <TableCell>{record.connector_id || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.siteName')}</strong></TableCell>
              <TableCell>{record.site_name || '-'}</TableCell>
            </TableRow>
            {record.charger && (
              <>
                <TableRow>
                  <TableCell><strong>{translate('resources.active-sessions.show.chargerPower')}</strong></TableCell>
                  <TableCell>{record.charger.power} kW</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.active-sessions.show.connectorType')}</strong></TableCell>
                  <TableCell>{record.charger.connector_type}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// User Information Card
const UserInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'secondary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Person sx={{ color: 'secondary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.active-sessions.show.userInfo')}</Typography>
        </Box>
        <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={detailsTableSx}>
          <TableBody>
            {(() => {
              const initiatedBy = record.initiated_by as string | undefined;
              const userTypeLabelMap: Record<string, { label: string; color: 'primary' | 'secondary' | 'info' | 'warning' | 'default' | 'success' }> = {
                app: { label: 'App User', color: 'primary' },
                rfid: { label: 'RFID', color: 'secondary' },
                vendor_api: { label: 'Vendor API', color: 'info' },
                admin: { label: 'Admin', color: 'warning' },
                backend_standalone: { label: 'Backend (Standalone)', color: 'warning' },
                backend_on_behalf: { label: 'Backend (On Behalf)', color: 'warning' },
                scheduled: { label: 'Scheduled', color: 'default' },
              };
              const meta = initiatedBy ? userTypeLabelMap[initiatedBy] : null;
              return meta ? (
                <TableRow>
                  <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.userType', { _: 'User Type' })}</strong></TableCell>
                  <TableCell>
                    <Chip size="small" color={meta.color} label={meta.label} />
                  </TableCell>
                </TableRow>
              ) : null;
            })()}
            {record.user && (() => {
              const userType = record.user.user_type as string | undefined;
              const userIdRaw = record.user.id as string | undefined;
              const displayId = userType === 'rfid'
                ? userIdRaw
                : (record.user.numeric_user_id != null ? `#${record.user.numeric_user_id}` : userIdRaw);
              if (!displayId) return null;
              const linkTo = userType === 'rfid' && userIdRaw
                ? `/rfid-users/${encodeURIComponent(userIdRaw)}`
                : (userType === 'app' && userIdRaw ? `/app-users/${encodeURIComponent(userIdRaw)}` : null);
              return (
                <TableRow>
                  <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.userId', { _: 'User ID' })}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>
                    {linkTo ? (
                      <RouterLink to={linkTo} style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {displayId}
                      </RouterLink>
                    ) : displayId}
                  </TableCell>
                </TableRow>
              );
            })()}
            {record.user && (
              <>
                <TableRow>
                  <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.name')}</strong></TableCell>
                  <TableCell>{record.user.name || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.active-sessions.show.email')}</strong></TableCell>
                  <TableCell>{record.user.email || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.active-sessions.show.phone')}</strong></TableCell>
                  <TableCell>{record.user.phone || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ verticalAlign: 'top' }}><strong>{translate('resources.active-sessions.show.vehicles', { _: 'Vehicles' })}</strong></TableCell>
                  <TableCell>
                    {Array.isArray(record.user.vehicles) && record.user.vehicles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {record.user.vehicles.map((v: any) => (
                          <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography component="span" sx={{ fontSize: 'inherit' }}>
                              {[v.make, v.model].filter(Boolean).join(' ')}
                            </Typography>
                            {v.license_plate && (
                              <Chip
                                size="small"
                                label={v.license_plate}
                                sx={{ fontFamily: 'monospace' }}
                              />
                            )}
                            {v.is_primary && (
                              <Chip size="small" color="primary" variant="outlined" label={translate('resources.active-sessions.show.primary', { _: 'Primary' })} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography component="span" color="text.secondary" sx={{ fontSize: 'inherit' }}>—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Cost Information Card
const CostInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'warning.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <AttachMoney sx={{ color: 'warning.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.active-sessions.show.costInfo')}</Typography>
        </Box>
        <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={detailsTableSx}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}><strong>{translate('resources.active-sessions.show.preAuthAmount')}</strong></TableCell>
              <TableCell>
                HKD {record.pre_auth_amount?.toFixed(2) || '0.00'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.currentEstimatedCost')}</strong></TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  HKD {record.estimated_cost?.toFixed(2) || '0.00'}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.finalCost')}</strong></TableCell>
              <TableCell>
                {record.final_cost
                  ? `HKD ${record.final_cost.toFixed(2)}`
                  : translate('resources.active-sessions.show.notFinalized')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{translate('resources.active-sessions.show.meterReading')}</strong></TableCell>
              <TableCell>
                {/* Backend returns meter_value in Wh (raw OCPP reading);
                    divide by 1000 to match the kWh label on the UI. */}
                {((record.meter_value ?? 0) / 1000).toFixed(3)} {translate('resources.active-sessions.show.kwh')}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Main Session Details Component
const SessionDetails = () => {
  const record = useRecordContext();
  const [manualStopOpen, setManualStopOpen] = useState(false);
  const [forceCompleteOpen, setForceCompleteOpen] = useState(false);

  const handleOpenManualStop = () => {
    if (record) {
      setManualStopOpen(true);
    }
  };

  const handleOpenForceComplete = () => {
    if (record) {
      setForceCompleteOpen(true);
    }
  };

  const sessionData = record ? {
    id: String(record.id),
    appSessionId: record.app_session_id,
    chargerName: record.charger_name,
    siteName: record.site_name,
    status: record.status,
    energyDelivered: record.energy_delivered,
    currentPower: record.current_power,
    durationSeconds: record.duration_seconds,
  } : null;

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: 900, margin: '0 auto', px: 0 }}>
        <PageHeader />
        <ChargingProgress
          onStopClick={handleOpenManualStop}
          onForceCompleteClick={handleOpenForceComplete}
        />
        <SessionInfo />
        <ChargerInfo />
        <UserInfo />
        <CostInfo />
      </Box>

      <ManualStopDialog
        open={manualStopOpen}
        onClose={() => setManualStopOpen(false)}
        session={sessionData}
      />

      <ForceCompleteDialog
        open={forceCompleteOpen}
        onClose={() => setForceCompleteOpen(false)}
        session={sessionData}
      />
    </>
  );
};

export const ActiveSessionsShow = () => (
  <Show
    title={false}
    actions={false}
    component="div"
    sx={{
      maxWidth: '100%',
      overflow: 'hidden',
      backgroundColor: 'transparent',
      '& .RaShow-main': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    }}
  >
    <SimpleShowLayout
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        },
        padding: { xs: 0, sm: 'inherit' },
      }}
    >
      <SessionDetails />
    </SimpleShowLayout>
  </Show>
);

export default ActiveSessionsShow;
